import { computed, ref } from "vue";
import { defineStore } from "pinia";
import Keycloak, {
  type KeycloakLoginOptions,
  type KeycloakLogoutOptions,
} from "keycloak-js";
import window from "@shared/utils/app-window.ts";
import oidcSafeRedirectUri from "@shared/utils/oidc-safe-redirect-uri.ts";
import logger from "@shared/utils/logger.ts";
import { keyCloakOrMock } from "@shared/stores/helpers/keyCloakOrMock.ts";
import { jwtDecode } from "jwt-decode";
import { LocalStorageKeys } from "@shared/types/localTypes/storage.ts";
import { useSessionLogic } from "@shared/use/useSessionLogic.ts";

export type KeycloakMock = {
  init: () => Promise<void>;
  login: (options?: KeycloakLoginOptions) => Promise<void>;
  createLoginUrl: (options?: KeycloakLoginOptions) => Promise<string>;
  logout: (options?: KeycloakLogoutOptions) => Promise<void>;
  updateToken: (token?: number) => Promise<void>;
  authenticated: boolean;
  token: string;
};

export interface JWTPayload {
  exp?: number; // Expires at
  iat?: number; // Issued at
  sub: string; // User ID
  jti?: string; // JWT ID
  iss?: string; // Issuer
  aud?: string; // Audience
  typ?: string; // Token type
  azp?: string; // Authorized party
  sid?: string; // Session ID
  scope?: string; // Scopes
  roles?: string[];
  email?: string;
  username?: string;
  idp_alias?: string;
  org?: string;
}

export const useKeycloakStore = defineStore("keycloak", () => {
  // State
  const initialState = keyCloakOrMock({
    _keycloak: new Keycloak({
      url: window.KEYCLOAK_URL,
      realm: window.KEYCLOAK_REALM,
      clientId: window.KEYCLOAK_CLIENT_ID,
    }),
    _isInitialized: false,
  });

  const _keycloak = ref<Keycloak | KeycloakMock | null>(initialState._keycloak);
  const _isInitialized = ref<boolean>(initialState._isInitialized);

  // Getters
  const keycloak = computed(() => _keycloak.value);
  const userId = computed(() => _keycloak.value);
  const tokenPayload = computed((): JWTPayload | null => {
    if (!_keycloak.value?.token) {
      return null;
    }
    try {
      return jwtDecode<JWTPayload>(_keycloak.value.token);
    } catch (e) {
      logger.warn("Failed to decode token", e);
      return null;
    }
  });

  // Actions
  const initializeKeycloak = async () => {
    if (_isInitialized.value) {
      return;
    }
    const { _keycloak: newKeycloak, _isInitialized: newIsInitialized } =
      keyCloakOrMock({
        _keycloak: new Keycloak({
          url: window.KEYCLOAK_URL,
          realm: window.KEYCLOAK_REALM,
          clientId: window.KEYCLOAK_CLIENT_ID,
        }),
        _isInitialized: false,
      });
    _keycloak.value = newKeycloak;
    _isInitialized.value = newIsInitialized;

    try {
      const isUserLoggedIn = await _keycloak.value?.init({
        onLoad: "check-sso",
        silentCheckSsoRedirectUri:
          window.location.origin + "/silent-check-sso.html",
        pkceMethod: "S256",
        useNonce: true,
      });
      if (isUserLoggedIn) {
        useSessionLogic().setLastSessionAction(
          LocalStorageKeys.LAST_SUCCESSFUL_LOGIN,
        );
      }
    } catch (e) {
      logger.error("Keycloak initialization error", e);
    }
    _isInitialized.value = true;
  };

  const login = async (options?: KeycloakLoginOptions) => {
    // Without an explicit target keycloak-js sends window.location.href as the redirect_uri,
    // which may still carry the callback parameters a previous Keycloak round trip left in the
    // address bar. Keep the user on the same page, minus those parameters.
    await _keycloak.value?.login({
      redirectUri: oidcSafeRedirectUri(window.location.href),
      ...options,
    });
  };

  const logout = async () => {
    // Without an explicit target keycloak-js sends window.location.href as the
    // post_logout_redirect_uri, which still carries the callback parameters Keycloak left in the
    // address bar. Every caller lands on the login page anyway, so ask for it directly.
    await _keycloak.value?.logout({
      redirectUri: `${window.location.origin}/login`,
    });
  };

  return {
    keycloak,
    userId,
    tokenPayload,
    initializeKeycloak,
    login,
    logout,
  };
});
